import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useCollaboratorAllocationsQuery } from '@/features/Collaborators/Allocations/data/useCollaboratorAllocationsQuery';
import { useResponsibleCollaboratorQuery } from '@/features/Collaborators/Data/hooks/useResponsibleEmailQuery';
import type { CollaboratorPublic } from '@/features/Collaborators/Data';
import {
  selectCurrentManagedAllocations,
  buildManagerSuggestions,
} from '@/features/Vacations/utils/managerSuggestions';

export interface ManagerSuggestion {
  collaborator: CollaboratorPublic;
  source: 'allocation' | 'registry';
  hint?: string;
}

interface UseManagerSuggestionsResult {
  suggestions: ManagerSuggestion[];
  hasMultipleAllocationManagers: boolean;
  isLoading: boolean;
}

async function fetchResponsibleCollaborator(
  responsibleId: string | null | undefined
): Promise<CollaboratorPublic | null> {
  if (!responsibleId) return null;

  const ref = doc(db, 'collaborators_public', responsibleId);
  const snap = await getDoc(ref);

  if (!snap.exists()) return null;

  return {
    ...(snap.data() as CollaboratorPublic),
    id: snap.id,
  };
}

export function useManagerSuggestions(
  collaboratorId?: string | null
): UseManagerSuggestionsResult {
  const { data: allocations, isLoading: allocationsLoading } =
    useCollaboratorAllocationsQuery(collaboratorId);
  const { data: registryResponsible, isLoading: registryLoading } =
    useResponsibleCollaboratorQuery(collaboratorId ?? undefined);

  const { data: allocationResponsibles = [] } = useQuery({
    queryKey: [
      'allocation_responsibles',
      allocations
        .map((a) => a.responsibleId)
        .filter(Boolean)
        .sort()
        .join(','),
    ],
    queryFn: async () => {
      const today = new Date();
      const currentAllocations = selectCurrentManagedAllocations(allocations, today);

      if (currentAllocations.length === 0) {
        return [];
      }

      const responsibles = await Promise.all(
        currentAllocations.map((alloc) =>
          fetchResponsibleCollaborator(alloc.responsibleId)
        )
      );

      return currentAllocations.map((alloc, idx) => ({
        allocation: alloc,
        responsible: responsibles[idx],
      }));
    },
    enabled: allocations.length > 0,
  });

  const suggestions = useMemo((): ManagerSuggestion[] => {
    return buildManagerSuggestions({
      allocationResponsibles,
      registryResponsible,
      excludeId: collaboratorId ?? undefined,
    });
  }, [allocationResponsibles, registryResponsible, collaboratorId]);

  const hasMultipleAllocationManagers = useMemo(() => {
    const today = new Date();
    const vigentManagers = new Set<string>();

    for (const alloc of allocations) {
      if (selectCurrentManagedAllocations([alloc], today).length > 0) {
        vigentManagers.add(alloc.responsibleId!);
      }
    }

    return vigentManagers.size > 1;
  }, [allocations]);

  return {
    suggestions,
    hasMultipleAllocationManagers,
    isLoading: allocationsLoading || registryLoading,
  };
}
